var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import DOMPurification from 'dompurify';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Button } from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { Input } from '@/components/Input/Input';
import { LanguageSelect } from '@/components/LanguageSelect/LanguageSelect';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useHandleLinksManually } from '@/hooks/useHandleLinksManually';
import { useSocket } from '@/hooks/useSocket';
import { visitorInformationSelector, themeSelector, isMfaSelector, variantSelector, } from '@/state/config';
import { emailState, isMobileState, sessionIdSelector, userSelector, } from '@/state/session';
import { getLocalDateTimeString, mergeDeep, setNestedValue, } from '@/util/utils';
import { useFieldRules } from './useFieldRules';
import styles from './WelcomeFields.module.scss';
export const WelcomeFields = () => {
    const sanitizer = DOMPurification.sanitize;
    const isMobile = useRecoilValue(isMobileState);
    const termsRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { errorMessage, fieldRules } = useFieldRules();
    const { updateContext } = useSocket();
    const sessionId = useRecoilValue(sessionIdSelector);
    const user = useRecoilValue(userSelector);
    const theme = useRecoilValue(themeSelector);
    const { headline, disclaimer, fields } = useRecoilValue(visitorInformationSelector);
    const { trackEvent } = useAnalytics();
    const setEmail = useSetRecoilState(emailState);
    const { handleLinkClick } = useHandleLinksManually();
    const { register, handleSubmit, reset, formState, getFieldState } = useForm();
    const { isNbg } = useRecoilValue(variantSelector);
    const isMfa = useRecoilValue(isMfaSelector);
    // Reset the loading if the sessionId changes
    useEffect(() => {
        queueMicrotask(() => setIsLoading(false));
    }, [sessionId]);
    useEffect(() => {
        reset(Object.assign(Object.assign({}, fields === null || fields === void 0 ? void 0 : fields.reduce((acc, { field, variable_type, variable }) => {
            var _a;
            if (field === 'custom') {
                const variableName = variable || 'custom';
                let value;
                if (variable_type === 'boolean') {
                    value = false;
                }
                else if (variable_type === 'date_time') {
                    value = getLocalDateTimeString(new Date());
                }
                if (variableName.includes('.')) {
                    // Build nested default for dotted paths
                    const nested = setNestedValue(variableName, value);
                    return mergeDeep(acc, nested);
                }
                return Object.assign(Object.assign({}, acc), { [variableName]: value });
            }
            return Object.assign(Object.assign({}, acc), { [field]: (user === null || user === void 0 ? void 0 : user[field]) &&
                    user[field] !== '***' &&
                    !((_a = user[field]) === null || _a === void 0 ? void 0 : _a.startsWith('Visitor '))
                    ? user[field]
                    : '' });
        }, {})), { disclaimer_accepted: false }));
    }, [fields, reset, user]);
    const isDisclaimerEnabled = !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled) && !!(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html);
    const onSubmit = useCallback((formData) => {
        // Change the type to `any` for easier handling
        trackEvent(AnalyticsEvent.WelcomeFormSubmitted);
        setIsLoading(true);
        const { display_name, email, phone, address, language, disclaimer_accepted } = formData, customFields = __rest(formData, ["display_name", "email", "phone", "address", "language", "disclaimer_accepted"]);
        const user = {
            display_name,
            email,
            phone,
            address,
            language,
        };
        // Cleanup any undefined values from the user object
        Object.keys(user).forEach((key) => user[key] === undefined && delete user[key]);
        if (user.language) {
            i18n.changeLanguage(user.language);
        }
        if (user.email) {
            setEmail(user.email);
        }
        // Update the context with the processed data
        updateContext(Object.assign({ user, global: Object.assign({ visitor_form_completed: true }, (isDisclaimerEnabled
                ? { disclaimer_accepted: disclaimer_accepted }
                : {})) }, customFields));
    }, [i18n, setEmail, trackEvent, updateContext, isDisclaimerEnabled]);
    const handleTermsChange = (e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked, {
            value: e.target.checked,
        });
    };
    const handleTermsLinkClick = useCallback((e) => {
        trackEvent(AnalyticsEvent.TermsAndConditionsLinkClicked);
        handleLinkClick(e);
    }, [handleLinkClick, trackEvent]);
    const { dirtyFields, errors } = formState;
    return (_jsx("div", { className: cn(styles.container, styles[theme], {
            [styles.isMfa]: isMfa,
            [styles.isNbg]: isNbg,
        }), children: _jsxs("form", { className: cn({ [styles.isMobile]: isMobile }), onSubmit: handleSubmit(onSubmit), autoComplete: "off", children: [headline && _jsx("p", { className: styles.headline, children: headline }), fields === null || fields === void 0 ? void 0 : fields.map((_a) => {
                    var { field, required } = _a, rest = __rest(_a, ["field", "required"]);
                    const name = field === 'display_name' ? 'name' : field;
                    if (field === 'language') {
                        return (_jsx(LanguageSelect, { label: t(`fields.${field}`), required: required, error: errors[field] ? errorMessage() : null, register: register(field, Object.assign({}, fieldRules(field, required))) }, field));
                    }
                    else if (field === 'custom') {
                        // For custom fields, use the variable name as the form field key
                        // This can be a JSON path like 'custom.field' or 'settings.theme'
                        // We keep it flat for form handling and convert to nested structure on submission
                        const variableName = rest.variable || 'custom';
                        // Use getFieldState to properly handle nested field paths for dirty state
                        const fieldState = getFieldState(variableName, formState);
                        if (rest.variable_type === 'boolean') {
                            return (_jsxs("div", { className: styles.checkbox, children: [_jsx(Checkbox, { id: variableName, register: register(variableName) }), _jsxs("label", { htmlFor: variableName, children: [(rest === null || rest === void 0 ? void 0 : rest.field_label) || t('fields.custom_field'), required && _jsx("span", { children: "*" })] })] }, variableName));
                        }
                        else {
                            // Handle text and number types
                            const type = (rest === null || rest === void 0 ? void 0 : rest.variable_type) === 'date_time'
                                ? 'datetime-local'
                                : rest === null || rest === void 0 ? void 0 : rest.variable_type;
                            return (_jsx(Input, { name: variableName, label: (rest === null || rest === void 0 ? void 0 : rest.field_label) || t('fields.custom_field'), 
                                // If the field is a datetime-local, we need to mark it as dirty in order to avoid the label hidding the input and show the picker
                                isDirty: fieldState.isDirty || type === 'datetime-local', required: required, type: type, error: errors[variableName] ? errorMessage() : null, register: register(variableName, Object.assign({}, fieldRules(variableName, required))) }, variableName));
                        }
                    }
                    return (_jsx(Input, { name: name, label: t(`fields.${field}`), isDirty: dirtyFields[field], required: required, error: errors[field] ? errorMessage() : null, register: register(field, Object.assign({}, fieldRules(field, required))) }, field));
                }), (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.enabled) && (disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html) && (_jsxs("div", { className: cn(styles.disclaimerContainer, {
                        [styles.error]: errors.disclaimer_accepted,
                    }), children: [_jsx(Checkbox, { onChange: handleTermsChange, className: "disclaimer-check" /* TestClassName.DisclaimerCheck */, register: register('disclaimer_accepted', {
                                required: true,
                            }) }), _jsx("div", { onClick: handleTermsLinkClick, role: "presentation", ref: termsRef, className: "disclaimer-container" /* TestClassName.DisclaimerContainer */, dangerouslySetInnerHTML: {
                                __html: sanitizer(disclaimer === null || disclaimer === void 0 ? void 0 : disclaimer.html, { ADD_ATTR: ['target'] }),
                            } })] })), _jsx(Button, { type: "submit", fullWidth: true, isLoading: isLoading, children: t('buttons.start') })] }) }));
};
